import { GraphQLQuery } from "@aws-amplify/api";
import { API } from 'aws-amplify';
import { useContext, useEffect, useState } from 'react';
import { Button, Container, Header, Input, List, Modal, ModalActions, ModalContent, ModalHeader } from "semantic-ui-react";
import { AccountInfo, CreateAccountInfoMutation, FreeQuotaMutation, ValidateRoleArnQuery } from '../API';
import { User } from '../auth/auth';
import { createAccountInfo, createUserInfo, freeQuota } from '../graphql/mutations';
import { validateRoleArn } from '../graphql/queries';
import { useChild } from '../hooks';
import { NavBar } from './NavBar';
import { useNavigate } from "react-router";

// arn:aws:iam::126347763417:role/unused-resource-detector-role

type OmitFields = {
  __typename: any,
  createdAt: string,
  updatedAt: string,
}

export type O<T> = Pick<T, Exclude<keyof T, keyof OmitFields>>;

export const AddAccount = () => {

  const [email] = useChild(useContext(User), 'email');
  const [userName] = useChild(useContext(User), 'userName')

  const [accountId, updateAccountId] = useState('');
  const [arn, updateArn] = useState('');
  const [accountName, setAccountName] = useState('');
  const [validated, setValidated] = useState(0);
  const [accountInfo, setAccountInfo] = useState<O<AccountInfo> | string>()
  const [addingAccount, setAddingAccount] = useState(false);

  const [alertMsg, setAlertMsg] = useState<undefined | string>(undefined)

  const navigate = useNavigate();

  const addAccountInfo = async () => {
    if (!accountInfo) return
    if (!userName) return
    if (typeof (accountInfo) === 'string') return

    setAddingAccount(true)

    // create user if not exists
    try {
      API.graphql({
        query: createUserInfo, variables: {
          input: {
            userId: email,
            userName: userName
          }
        }
      }); 
    } catch (e) {
      // do nothing, the user might already exist
    }

    // add aws account
    try {
      const result = await API.graphql<GraphQLQuery<CreateAccountInfoMutation>>({
        query: createAccountInfo, variables: {
          input: { ...accountInfo, accountName, userInfoAccountsUserId: email }
        }
      });
      await API.graphql<GraphQLQuery<FreeQuotaMutation>>({
        query: freeQuota,
        variables: {
          accountId: accountId
        }
      })
      if (result.errors) {
        setAlertMsg(`You have alread provided a role for the AWS account ${accountId}. If you want to change the role, please delete the account and re-add.`)
      } else {
        setAlertMsg(`Successfully added account ${accountId}`)
      }
    } catch (error: any) {
      if (error.errors) {
        setAlertMsg(`You have alread provided a role for the AWS account ${accountId}. If you want to change the role, please delete the account and re-add.`)
      } else {
        setAlertMsg(`Failed to add account ${accountId}, please try again later or contact support`)
      }
      // console.log(error)
    }

    setAddingAccount(false);
  }

  const doValidateRoleArn = async () => {
    if (!arn) {
      setAlertMsg('Role arn is empty');
      return;
    }
    setValidated(-1);
    const resp = await API.graphql<GraphQLQuery<ValidateRoleArnQuery>>({
      query: validateRoleArn,
      variables: {
        roleArn: arn
      }
    });
    if (resp.data?.validateRoleArn?.statusCode === 403) {
      setValidated(0);
      setAlertMsg('Invalid role arn');
      return;
    }
    setValidated(1);
    setAlertMsg(undefined);
  }

  useEffect(() => {
    setValidated(0);
    if (accountId === '') return;

    const accountInfo: O<AccountInfo> = {
      accountId,
      monitorRoleArn: arn,
      accountName: accountName || accountId
    }
    setAccountInfo(accountInfo);
  }, [arn, accountId])

  useEffect(() => {
    setAccountInfo({ ...(accountInfo as any || {}), accountName })
  }, [accountName])

  return (
    <Container>
      <NavBar />
      <Header as='h1'>Add New Account for {userName}</Header>
      <List>

        <List.Item>
          <Input value={accountId} onChange={(value) => updateAccountId(value.target.value)} placeholder='AccountId' />
        </List.Item>
        <List.Item>
          <List horizontal>
          <List.Item><Input value={arn} onChange={(value) => updateArn(value.target.value)} placeholder='Arn' /></List.Item>
          <List.Item><Button onClick={() => {
            // check if accountId is a valid aws account id, with regex
            if (!accountId.match(/^[0-9]{12}$/)) {
              setAlertMsg('Invalid Account Id: ' + accountId);
              return;
            }
            window.open(`https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/quickcreate?templateURL=https://optispend-cfn-cross-account-role.s3.amazonaws.com/template.yaml&stackName=ServiceProviderRole&param_ServiceProviderAccount=${accountId}`, '_blank');

          }}>Create the Account Role</Button></List.Item>
          </List>
        </List.Item>
        <List.Item>
          <Input value={accountName} onChange={(value) => setAccountName(value.target.value)} placeholder='AccountName' />
        </List.Item>
        <List.Item>
          <Button loading={validated === -1} onClick={() => doValidateRoleArn()}>Validate Role</Button>
        </List.Item>
        { validated === 1 && <List.Item>
          <List.Icon name='check circle' />
          <List.Content>Role is valid to use.</List.Content>
          </List.Item>}
        <List.Item>
         <Button primary loading={addingAccount} disabled={validated !== 1} onClick={() => addAccountInfo()}>Submit</Button>
        </List.Item>
      </List>

      
      
      <div>
        {accountInfo !== undefined
          && typeof (accountInfo) === 'string'
          && accountInfo.length === 0
          ? accountInfo : ''}
      </div>

      {/* confirm information */}
        {(accountInfo && (accountInfo as O<AccountInfo>).accountId) && <List>
          <List.Item><Header as='h2'>Confirm Account Information</Header></List.Item>
          <List.Item><List.Header>AccountId</List.Header><List.Content>{(accountInfo as O<AccountInfo>).accountId}</List.Content></List.Item>
          <List.Item><List.Header>Account Montior Role</List.Header><List.Content>{(accountInfo as O<AccountInfo>).monitorRoleArn}</List.Content></List.Item>
          <List.Item><List.Header>AccountName</List.Header><List.Content>{(accountInfo as O<AccountInfo>).accountName}</List.Content></List.Item>
          </List>}


      <Modal
        size={'mini'}
        open={alertMsg !== undefined}
      >
        <ModalHeader>Something goes wrong</ModalHeader>
        <ModalContent>
          <p>{alertMsg}</p>
        </ModalContent>
        <ModalActions>
          <Button positive onClick={() => setAlertMsg(undefined)}>
            Okay
          </Button>
        </ModalActions>
      </Modal>
    </Container>
  )
}